import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Loader2, Phone, Clock, CheckCircle2, XCircle, MessageSquare } from "lucide-react";
import { api } from "@/lib/api";

const STATUS_LABELS = {
  pending_review: { label: "Pending Review", cls: "bg-amber-100 text-amber-900 border-amber-300" },
  contacted:      { label: "Contacted",      cls: "bg-blue-100 text-blue-900 border-blue-300" },
  scheduled_offline: { label: "Scheduled (Offline)", cls: "bg-emerald-100 text-emerald-900 border-emerald-300" },
  declined:       { label: "Declined",       cls: "bg-rose-100 text-rose-900 border-rose-300" },
  closed:         { label: "Closed",         cls: "bg-slate-100 text-slate-700 border-slate-300" },
};

export default function BookingRequests() {
  const [items, setItems] = useState(null);
  const [filter, setFilter] = useState("all");

  const load = async () => {
    try {
      const { data } = await api.get(`/admin/booking-requests`);
      setItems(data);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to load booking requests");
      setItems([]);
    }
  };
  useEffect(() => { load(); }, []);

  const update = async (id, payload) => {
    try {
      await api.patch(`/admin/booking-requests/${id}`, payload);
      toast.success("Updated");
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Update failed");
    }
  };

  if (items === null) return <div className="p-6 text-slate-400 flex items-center gap-2"><Loader2 className="animate-spin" size={16}/> Loading…</div>;

  const filtered = filter === "all" ? items : items.filter(b => b.status === filter);
  const counts = items.reduce((acc, b) => { acc[b.status] = (acc[b.status] || 0) + 1; return acc; }, {});

  return (
    <div data-testid="admin-booking-requests-page">
      <div className="flex items-center justify-between mb-5">
        <div>
          <h1 className="font-display text-2xl font-bold text-slate-100">Booking Requests</h1>
          <p className="text-sm text-slate-400 mt-1">Customer-submitted requests awaiting operator follow-up. No payment is collected on these; the customer is contacted by phone.</p>
        </div>
        <div className="text-right">
          <div className="text-xs uppercase text-slate-500 tracking-widest font-bold">Pending Review</div>
          <div className="font-mono text-3xl font-bold text-amber-400" data-testid="pending-review-count">{counts.pending_review || 0}</div>
        </div>
      </div>

      <div className="flex gap-2 mb-4 flex-wrap">
        <FilterChip label={`All (${items.length})`} active={filter === "all"} onClick={() => setFilter("all")} testid="filter-all"/>
        {Object.entries(STATUS_LABELS).map(([k, v]) => (
          <FilterChip key={k} label={`${v.label} (${counts[k] || 0})`} active={filter === k} onClick={() => setFilter(k)} testid={`filter-${k}`}/>
        ))}
      </div>

      {filtered.length === 0 ? (
        <Card className="admin-card p-10 text-center text-slate-400" data-testid="empty-state">
          No booking requests in this view.
        </Card>
      ) : (
        <div className="space-y-3">
          {filtered.map(b => (
            <RequestRow key={b.id} b={b} onUpdate={update}/>
          ))}
        </div>
      )}
    </div>
  );
}

const FilterChip = ({ label, active, onClick, testid }) => (
  <button data-testid={testid} onClick={onClick}
    className={`px-3 py-1.5 rounded-full text-xs font-semibold border transition ${active ? "bg-orange-500 text-white border-orange-500" : "bg-slate-900 text-slate-300 border-slate-700 hover:border-slate-500"}`}>
    {label}
  </button>
);

function RequestRow({ b, onUpdate }) {
  const [note, setNote] = useState(b.admin_note || "");
  const [status, setStatus] = useState(b.status || "pending_review");
  const s = STATUS_LABELS[b.status] || STATUS_LABELS.pending_review;

  return (
    <Card className="admin-card p-4" data-testid={`request-row-${b.code}`}>
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div className="min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="font-mono font-bold text-slate-100" data-testid="request-code">{b.code}</span>
            <span className={`text-[11px] font-bold px-2 py-0.5 rounded border ${s.cls}`} data-testid="request-status-pill">{s.label}</span>
            <span className="text-xs text-slate-500">{new Date(b.created_at).toLocaleString()}</span>
          </div>
          <div className="mt-2 grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-1 text-sm">
            <Field k="Customer" v={b.customer_name}/>
            <Field k="Phone" v={<a href={`tel:${b.customer_phone}`} className="text-orange-400 hover:underline inline-flex items-center gap-1"><Phone size={12}/>{b.customer_phone}</a>}/>
            <Field k="Service" v={b.pricing?.service_label || b.category}/>
            <Field k="Workers" v={b.worker_count}/>
            <Field k="When" v={`${b.scheduled_date} · ${b.start_time} · ${b.duration_hours}h`}/>
            <Field k="Location" v={b.location || "—"}/>
            <Field k="Est. total" v={<span className="font-mono">₹{b.amount}</span>}/>
            <Field k="Payment" v={<span className="text-slate-400">Not collected</span>}/>
          </div>
          {b.work_description && <div className="mt-2 text-xs text-slate-400"><span className="text-slate-500 font-semibold">Work: </span>{b.work_description}</div>}
          {b.additional_notes && <div className="mt-1 text-xs text-slate-400"><span className="text-slate-500 font-semibold">Notes: </span>{b.additional_notes}</div>}
          {b.admin_note && <div className="mt-2 text-xs text-amber-400"><span className="text-amber-600 font-semibold">Admin note: </span>{b.admin_note}</div>}
        </div>
        <div className="flex items-center gap-2 shrink-0">
          <Dialog>
            <DialogTrigger asChild>
              <Button size="sm" variant="outline" data-testid={`manage-${b.code}`}><MessageSquare size={14} className="mr-1"/>Manage</Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader><DialogTitle>Manage Request · {b.code}</DialogTitle></DialogHeader>
              <div className="space-y-3">
                <div>
                  <label className="text-xs font-semibold text-slate-600">Status</label>
                  <Select value={status} onValueChange={setStatus}>
                    <SelectTrigger data-testid="manage-status-select"><SelectValue/></SelectTrigger>
                    <SelectContent>
                      {Object.entries(STATUS_LABELS).map(([k, v]) => (
                        <SelectItem key={k} value={k}>{v.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <label className="text-xs font-semibold text-slate-600">Admin note (visible to operators only)</label>
                  <Textarea rows={3} value={note} onChange={(e) => setNote(e.target.value)} placeholder="e.g. Called customer at 2pm; confirmed Thursday 10am" data-testid="manage-note-input"/>
                </div>
                <div className="flex justify-end gap-2 pt-2">
                  <Button className="bg-orange-500 hover:bg-orange-600" onClick={() => onUpdate(b.id, { status, note })} data-testid="manage-save-btn">Save</Button>
                </div>
              </div>
            </DialogContent>
          </Dialog>
          {b.status === "pending_review" && (
            <>
              <Button size="sm" className="bg-blue-600 hover:bg-blue-700" onClick={() => onUpdate(b.id, { status: "contacted" })} data-testid={`mark-contacted-${b.code}`}><Phone size={14} className="mr-1"/>Mark Contacted</Button>
            </>
          )}
          {b.status === "contacted" && (
            <Button size="sm" className="bg-emerald-600 hover:bg-emerald-700" onClick={() => onUpdate(b.id, { status: "scheduled_offline" })} data-testid={`mark-scheduled-${b.code}`}><CheckCircle2 size={14} className="mr-1"/>Scheduled</Button>
          )}
          {["pending_review","contacted"].includes(b.status) && (
            <Button size="sm" variant="outline" onClick={() => onUpdate(b.id, { status: "declined" })} data-testid={`decline-${b.code}`}><XCircle size={14} className="mr-1"/>Decline</Button>
          )}
        </div>
      </div>
    </Card>
  );
}

const Field = ({ k, v }) => (
  <div className="flex items-center gap-2">
    <span className="text-slate-500 text-xs">{k}:</span>
    <span className="text-slate-200">{v}</span>
  </div>
);
